import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Countdown } from '@liveauctioneers/hammer-ui-core/countdown';
import { useFeatureFlags } from '@liveauctioneers/hammer-ui-core/featureFlags/hooks';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { LabelStrong } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { EventContext } from '@/redux/modules/analytics';
import { loadPageData } from '@/redux/modules/appState';
import {
    getCatalogIsInstantAutopay,
    getCatalogIsTimedPlusBiddingOpen,
    getCatalogIsTimedPlusClosing,
    getCatalogStartTime,
    getCatalogTimedPlusBiddingEndsFromTs,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import {
    getIsRegistered,
    getRegistrationSuspended,
} from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { CompleteAccountModalContext, ModalContext, openRegisterForAuctionModal } from '@/redux/modules/modal';
import { getIsWhiteLabel } from '@/redux/modules/whiteLabel';
import { useTrackRegisterForAuctionPressedAnalytics } from '@/utils/amplitude/analytics/registration';
import { useAmplitudeExperiments } from '@/utils/amplitude/experiments/utils/useAmplitudeExperiments';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { CatalogRegistrationMessages } from './CatalogRegistrationMessages';
import { CatalogStatusBar } from './CatalogStatusBar';

type Props = {
    catalogId: number;
    className?: string;
    setForceIsLive: (force: boolean) => void;
};

export const TimedPlusAuctionStatus = ({ catalogId, className, setForceIsLive }: Props) => {
    const { variants } = useAmplitudeExperiments();
    const formatMessage = useAppFormatMessage();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const featureFlags = useFeatureFlags();
    const reloadPageData = useCallback(() => {
        dispatch(loadPageData({ amplitudeExperimentFlags: variants, featureFlags, location }));
    }, [dispatch, location, variants, featureFlags]);
    const isWhiteLabel = useAppSelector(getIsWhiteLabel);
    const registered = useAppSelector((state) => getIsRegistered(state, catalogId));
    const suspended = useAppSelector((state) => getRegistrationSuspended(state, catalogId));
    const biddingEndsFrom = useAppSelector((state) => getCatalogTimedPlusBiddingEndsFromTs(state, catalogId));
    const biddingOpens = useAppSelector((state) => getCatalogStartTime(state, catalogId));
    const isInstantAutopay = useAppSelector((state) => getCatalogIsInstantAutopay(state, catalogId));

    const timedPlusBiddingOpen = useAppSelector((state) => getCatalogIsTimedPlusBiddingOpen(state, catalogId));
    const lotsAreClosing = useAppSelector((state) => getCatalogIsTimedPlusClosing(state, catalogId));
    const trackRegisterForAuctionPressedAnalytics = useTrackRegisterForAuctionPressedAnalytics();

    const modalContext = useMemo<ModalContext>(
        () => ({
            catalogId,
            eventContext: EventContext.registerForAuction,
            id: catalogId,
            type: CompleteAccountModalContext.RegisterForAuction,
        }),
        [catalogId]
    );

    const registerForCatalog = () => {
        trackRegisterForAuctionPressedAnalytics();
        dispatch(
            openRegisterForAuctionModal({
                catalogId,
                fromBiddingConsole: false,
                isInstantAutopay,
                modalContext,
            })
        );
    };

    return (
        <div
            className={cn('flex h-full max-w-full flex-col items-center justify-start', className)}
            data-testid="timed-plus-auction-status"
            suppressHydrationWarning
        >
            {registered && <CatalogRegistrationMessages catalogId={catalogId} />}
            <LabelStrong className="mb-xs">
                {formatMessage(['catalogPage', 'catalogStatus', 'timedPlusAuction'], {
                    values: { numberOfAuctions: 1 },
                })}
            </LabelStrong>
            <div className="tracking-[1px] text-text-secondary uppercase">
                {formatMessage([
                    'catalogPage',
                    'catalogStatus',
                    lotsAreClosing ? 'inProgress' : timedPlusBiddingOpen ? 'timedPlusBiddingEnds' : 'timedPlusStarts',
                ])}
            </div>
            {lotsAreClosing ? (
                <CatalogStatusBar catalogId={catalogId} />
            ) : (
                <div
                    className="mt-sm mb-lg flex justify-center [&_.hui-text-body-secondary]:hui-text-h5"
                    data-testid="countdown"
                >
                    <Countdown
                        completionTime={(timedPlusBiddingOpen ? biddingEndsFrom : biddingOpens) * 1000}
                        forceAllUnits
                        onComplete={() => {
                            setForceIsLive(true);
                            reloadPageData();
                        }}
                        small
                        timeLabels={{ days: ' days', hours: ' hrs', minutes: ' mins', seconds: ' secs' }}
                    />
                </div>
            )}
            {suspended ? (
                <Button
                    className="mb-sm w-full"
                    data-testid="catalogAccountSuspendedButton"
                    disabled
                    variant="filled"
                >
                    {formatMessage(['account_suspended'])}
                </Button>
            ) : (
                !registered && (
                    <Button
                        className="mb-sm w-full"
                        data-testid="liveCatalogRegisterButton"
                        onClick={registerForCatalog}
                        style={isWhiteLabel ? 'primary' : 'critical'}
                        variant="filled"
                    >
                        {formatMessage(['catalogPage', 'catalogStatus', 'registerToBid'])}
                    </Button>
                )
            )}
            <Link
                href="https://support.liveauctioneers.com/hc/en-gb/articles/24574071591313-How-do-Timed-auctions-work"
                rel="noopener"
                target="_blank"
            >
                {formatMessage(['catalogPage', 'catalogStatus', 'aboutTimedPlus'])}
            </Link>
        </div>
    );
};
